import {
  createContext,
  useCallback,
  useContext,
  useState,
  FormEvent,
  MouseEvent,
  useEffect,
  SetStateAction,
  useMemo,
  Dispatch,
} from "react";
import { useLocation } from "react-router-dom";
import { useAuth } from "../../../hooks/AuthHooks";
import useLocalStorage from "../../../hooks/useLocalStorage";

type EventHandler = (
  _?: FormEvent<HTMLFormElement> | MouseEvent<HTMLButtonElement>
) => void;

type MFATontextType = {
  consumer: {
    activateTotpWall: EventHandler;
    submitEventSetter: (_: EventHandler) => EventHandler;
    submitEventSetterComisiones: (_: EventHandler) => EventHandler;
    submitEventSetterComisionesUsuarioPadre: (_: EventHandler) => EventHandler;
  };
  api: {
    activeModal: boolean;
    submitEvent: EventHandler;
    setCurrentTotp: Dispatch<SetStateAction<string | null>>;
    deactivateTotpWall: () => void;
  };
};

export const MFAContext = createContext<MFATontextType>({
  consumer: {
    activateTotpWall: () => {},
    submitEventSetter: (_) => _,
    submitEventSetterComisiones: (_) => _,
    submitEventSetterComisionesUsuarioPadre: (_) => _,
  },
  api: {
    activeModal: false,
    submitEvent: (_) => {},
    setCurrentTotp: (_) => {},
    deactivateTotpWall: () => {},
  },
});

export const useMFA = () => {
  return useContext(MFAContext).consumer;
};
export const useMFAApi = () => {
  return useContext(MFAContext).api;
};

export const useProvideMFA = () => {
  const { commerceInfo, pdpUser } = useAuth();

  const commUseTotp = useMemo(
    () => (commerceInfo as any)?.use_totp ?? true,
    [commerceInfo]
  );

  const commUseTotpComisiones = useMemo(
    () => (commerceInfo as any)?.use_otp_comisiones ?? true,
    [commerceInfo]
  );

  const userUseTotpComisiones = useMemo(
    () => (pdpUser as any)?.use_otp_comisiones ?? true,
    [pdpUser]
  );

  const { pathname } = useLocation();

  const [activeModal, setActiveModal] = useState(false);
  const [, setCurrentTotp] = useLocalStorage<string | null>(
    "current_totp",
    null
  );
  const [submitEvent, setSubmitEvent] = useState<EventHandler>(() => () => {});
  const [, setVerifyOtpFetch] = useLocalStorage<boolean>(
    "commerce_use_totp",
    false
  );

  // Otp para trxs de cash in
  const [commerceUseTotp, setCommerceUseTotp] = useState<boolean>(true);

  // Otp para trxs de dispersion de comision comercios
  const [commerceUseTotpComisiones, setCommerceUseTotpComisiones] =
    useState<boolean>(true);

  // Otp para trxs de dispersion de comision usuarios padre
  const [
    userUseTotpComisionesUsuarioPadre,
    setCommerceUseTotpComisionesUsuarioPadre,
  ] = useState<boolean>(true);

  const activateTotpWall = useCallback<EventHandler>((ev) => {
    if (ev && ev.type === "submit") {
      ev.preventDefault();
    }
    setActiveModal(true);
  }, []);
  const deactivateTotpWall = useCallback(() => {
    setActiveModal(false);
  }, []);

  // Submitter para trxs de cash in
  const submitEventSetter = useCallback(
    (onSubmit: EventHandler) => {
      if (!commerceUseTotp) {
        setVerifyOtpFetch(false);
        return onSubmit;
      }
      setSubmitEvent(() => onSubmit);
      setVerifyOtpFetch(true);
      return activateTotpWall;
    },
    [activateTotpWall, commerceUseTotp, setVerifyOtpFetch]
  );
  // Submitter para trxs de dispersion de comision comercios
  const submitEventSetterComisiones = useCallback(
    (onSubmit: EventHandler) => {
      if (!commerceUseTotpComisiones) {
        setVerifyOtpFetch(false);
        return onSubmit;
      }
      setSubmitEvent(() => onSubmit);
      setVerifyOtpFetch(true);
      return activateTotpWall;
    },
    [activateTotpWall, commerceUseTotpComisiones, setVerifyOtpFetch]
  );
  // Submitter para trxs de dispersion de comision usuarios padre
  const submitEventSetterComisionesUsuarioPadre = useCallback(
    (onSubmit: EventHandler) => {
      if (!userUseTotpComisionesUsuarioPadre) {
        setVerifyOtpFetch(false);
        return onSubmit;
      }
      setSubmitEvent(() => onSubmit);
      setVerifyOtpFetch(true);
      return activateTotpWall;
    },
    [activateTotpWall, userUseTotpComisionesUsuarioPadre, setVerifyOtpFetch]
  );

  useEffect(() => {
    setSubmitEvent(() => () => {});
    setCurrentTotp(null);
    setVerifyOtpFetch(false);
  }, [pathname, setCurrentTotp, setVerifyOtpFetch]);

  useEffect(() => {
    setCommerceUseTotp(commUseTotp ?? false);
  }, [commUseTotp, setCommerceUseTotp]);

  useEffect(() => {
    setCommerceUseTotpComisiones(commUseTotpComisiones ?? false);
  }, [commUseTotpComisiones, setCommerceUseTotpComisiones]);

  useEffect(() => {
    setCommerceUseTotpComisionesUsuarioPadre(userUseTotpComisiones ?? false);
  }, [userUseTotpComisiones, setCommerceUseTotpComisionesUsuarioPadre]);

  return {
    consumer: {
      activateTotpWall,
      submitEventSetter,
      submitEventSetterComisiones,
      submitEventSetterComisionesUsuarioPadre,
    },
    api: {
      activeModal,
      submitEvent,
      setCurrentTotp,
      deactivateTotpWall,
    },
  };
};

